import { DeployVersionBootstrap } from "@/app/DeployVersionBootstrap";
import { OfflineProvider } from "@/domains/offline/connectivity/OfflineProvider";
import { queryClient } from "@/domains/shared/services/queryClient";
import { ensureCsrfToken, isCsrfRequired } from "@/domains/shared/services/setupCsrfInterceptor";
import { i18next, resolveTranslationLanguage } from "@mivo/i18n";
import { QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";

function CsrfBootstrap() {
  useEffect(() => {
    if (!isCsrfRequired()) {
      return;
    }

    void ensureCsrfToken().catch(() => {
      /* login/public pages will retry on first mutating request */
    });
  }, []);

  return null;
}

/**
 * i18next observer: subscribes to language changes without remounting children
 * (preserves form local state).
 *
 * React bails out of re-rendering stable `children` prop elements, so this alone
 * does not refresh `getTranslation` copy. Route roots (e.g. `App`) and any
 * component that calls `getTranslation` must also call `useLocale()` /
 * `useTranslation()` so their JSX tree re-renders on locale change.
 */
function LocaleObserver({ children }: { children: ReactNode }) {
  useTranslation();
  resolveTranslationLanguage(i18next.language);
  return <>{children}</>;
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <I18nextProvider i18n={i18next}>
      <QueryClientProvider client={queryClient}>
        <CsrfBootstrap />
        <DeployVersionBootstrap />
        <LocaleObserver>
          <OfflineProvider>{children}</OfflineProvider>
        </LocaleObserver>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
